import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import { IContactFormData } from "../../../../types/contact";
import { Venue } from "../../../../types/venue";

export const useContactSearch = () => {
  const [contactOptions, setContactOptions] = useState<IContactFormData[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (contactSearchTerm.length < 3) {
        setContactOptions([]);
        return;
      }
      setContactLoading(true);
      try {
        const res = await apiService.get<{ results: IContactFormData[] }>(
          `/api/contacts/contact/?search=${contactSearchTerm}`,
        );
        setContactOptions(res.data.results || []);
      } catch {
        toast.error("Failed to load contacts. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setContactLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [contactSearchTerm]);

  const addContactToOptions = (contact: IContactFormData) =>
    setContactOptions((prev) => [contact, ...prev]);

  return {
    contactOptions,
    contactLoading,
    setContactSearchTerm,
    addContactToOptions,
  };
};

export const useCompanySearch = () => {
  const [companyOptions, setCompanyOptions] = useState<IContactFormData[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (companySearchTerm.length < 3) {
        setCompanyOptions([]);
        return;
      }
      setCompanyLoading(true);
      try {
        const res = await apiService.get<{ results: IContactFormData[] }>(
          `/api/contacts/contact/?search=${companySearchTerm}&contact_type=company`,
        );
        setCompanyOptions(res.data.results || []);
      } catch {
        toast.error("Failed to load companies. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setCompanyLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [companySearchTerm]);

  const addCompanyToOptions = (company: IContactFormData) =>
    setCompanyOptions((prev) => [company, ...prev]);

  return {
    companyOptions,
    companyLoading,
    setCompanySearchTerm,
    addCompanyToOptions,
  };
};

export const useVenueSearch = (initialVenueId?: string) => {
  const [venueOptions, setVenueOptions] = useState<Venue[]>([]);
  const [venueLoading, setVenueLoading] = useState(false);
  const [venueSearchTerm, setVenueSearchTerm] = useState("");
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);

  // Load initial venue in edit mode
  useEffect(() => {
    if (!initialVenueId || selectedVenue) return;
    const fetchInitialVenue = async () => {
      setVenueLoading(true);
      try {
        const res = await apiService.get<Venue>(
          `/api/venues/venue/${initialVenueId}/`,
        );
        setSelectedVenue(res.data);
        setVenueOptions([res.data]);
      } catch {
        toast.error("Failed to load venue.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setVenueLoading(false);
      }
    };
    fetchInitialVenue();
  }, [initialVenueId, selectedVenue]);

  // Search effect
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (venueSearchTerm.length < 3) {
        setVenueOptions([]);
        return;
      }
      setVenueLoading(true);
      try {
        const res = await apiService.get<{ results: Venue[] }>(
          `/api/venues/venue/?search=${venueSearchTerm}`,
        );
        setVenueOptions(res.data.results || []);
      } catch {
        toast.error("Failed to load venues. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setVenueLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [venueSearchTerm]);

  return {
    venueOptions,
    venueLoading,
    selectedVenue,
    setSelectedVenue,
    setVenueSearchTerm,
  };
};

export interface ICompanyContactOption {
  id: number;
  full_name: string;
  company_id: number | null;
  company_name: string | null;
  branch_name: string | null;
  role: string | null;
  email: string | null;
  phone: string | null;
}

interface CompanyRepResponse {
  id: number;
  full_name: string;
  email: string;
  role: string | null;
  phone: string;
  branch_name: string;
}

export const useCompanyContactSearch = (
  companyId?: number | null,
  companyName?: string | null,
) => {
  const [options, setOptions] = useState<ICompanyContactOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setLoading(true);
    apiService
      .get<{ results: CompanyRepResponse[] }>(
        `/api/contacts/contact/by-company/?company_id=${companyId}`,
      )
      .then((res) => {
        if (cancelled) return;
        setOptions(
          (res.data.results || []).map((c) => ({
            id: c.id,
            full_name: c.full_name,
            company_id: companyId,
            company_name: companyName ?? null, // ← was null, now real
            branch_name: c.branch_name || null,
            role: c.role || null,
            email: c.email || null,
            phone: c.phone || null,
          })),
        );
      })

      .catch(() =>
        toast.error("Failed to load company representatives.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        }),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [companyId, companyName]);

  // Free-text mode: no company known yet — search across all companies.
  useEffect(() => {
    if (companyId) return;
    const timer = setTimeout(async () => {
      if (searchTerm.length < 3) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const res = await apiService.get<ICompanyContactOption[]>(
          `/api/contacts/contact/company-contact-search/?q=${encodeURIComponent(searchTerm)}`,
        );
        setOptions(res.data || []);
      } catch {
        toast.error("Failed to load company contacts. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, companyId]);

  return {
    companyContactOptions: options,
    companyContactLoading: loading,
    setCompanyContactSearchTerm: setSearchTerm,
  };
};

interface IReferralPartner {
  id: number;
  referral_name: string;
  referral_surname: string;
}

export const useReferralSearch = () => {
  const [referralOptions, setReferralOptions] = useState<IReferralPartner[]>(
    [],
  );
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralSearchTerm, setReferralSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      if (referralSearchTerm.length >= 3) {
        fetchReferralPartners(referralSearchTerm);
      } else {
        setReferralOptions([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [referralSearchTerm]);

  const fetchReferralPartners = async (search: string) => {
    setReferralLoading(true);
    try {
      const response = await apiService.get<{ results: IReferralPartner[] }>(
        `/api/refferal-program/refferal-programs/?search=${search}`,
      );
      setReferralOptions(response.data.results || []);
    } catch (err) {
      console.error("Failed to fetch referral partners:", err);
      toast.error("Failed to load referral partners. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    } finally {
      setReferralLoading(false);
    }
  };

  return {
    referralOptions,
    referralLoading,
    setReferralSearchTerm,
  };
};
export interface IFuneralHomeCompanyOption {
  id: number;
  name: string;
  company_logo?: string | null;
  city?: string | null;
}

export const useFuneralHomeCompanies = () => {
  const [companies, setCompanies] = useState<IFuneralHomeCompanyOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const url =
          searchTerm.length >= 2
            ? `/api/companies/company/?company_type=funeral_home&search=${encodeURIComponent(searchTerm)}&page_size=50`
            : `/api/companies/company/?company_type=funeral_home&page_size=50`;
        const res = await apiService.get<{
          results: IFuneralHomeCompanyOption[];
        }>(url);
        setCompanies(res.data.results || []);
      } catch {
        toast.error("Failed to load funeral homes.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  return {
    funeralHomeCompanies: companies,
    funeralHomeLoading: loading,
    setFuneralHomeSearchTerm: setSearchTerm,
  };
};

export interface IFuneralHomeBranchOption {
  id: number;
  name: string;
}

export const useCompanyBranches = (companyId?: number | null) => {
  const [branches, setBranches] = useState<IFuneralHomeBranchOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) {
      setBranches([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiService
      .get<{ results: IFuneralHomeBranchOption[] }>(
        `/api/companies/company-branch/?company=${companyId}`,
      )
      .then((res) => {
        if (!cancelled) setBranches(res.data.results || []);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load branches.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return { branches, branchesLoading: loading };
};
